import { useMemo, useRef, useState, type CSSProperties } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   The tick off. Ben asked (2026-10-27) for a ticked to-do to clearly tick,
   then swipe away and be gone, "for my endorphins", instead of dropping to
   the bottom. Jay picked this one from a prototype the next day: the box pops
   and glows, sparks burst out, a sheen runs along the line while it is struck
   through, then the line swipes right and the gap closes.

   The animation runs alongside the save, and the list is only read again once
   both are over, so the row is never pulled out from under its own animation.
   ────────────────────────────────────────────────────────────────────────── */

const reduced = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useLeave<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [active, setActive] = useState(false);

  /** Plays the whole tick and resolves once the row has folded away. */
  const play = async () => {
    setActive(true);
    const quiet = reduced();
    // Let the tick land and be seen before the line leaves.
    await wait(quiet ? 250 : 720);
    const row = ref.current;
    if (!row?.isConnected) return;
    const out = quiet
      ? row.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: "forwards" })
      : row.animate(
          [
            { transform: "translateX(0)", opacity: 1, filter: "blur(0)" },
            { transform: "translateX(-6px)", opacity: 1, filter: "blur(0)", offset: 0.18 },
            { transform: "translateX(72px)", opacity: 0, filter: "blur(3px)" },
          ],
          { duration: 380, easing: "cubic-bezier(0.55, 0, 0.75, 0.2)", fill: "forwards" },
        );
    await out.finished;
    if (!row.isConnected) return;
    // Folding the row's own height lets the lines below slide up with it, in both lists.
    const height = `${row.offsetHeight}px`;
    row.style.overflow = "hidden";
    const fold = row.animate(
      [
        { height, minHeight: height },
        { height: "0px", minHeight: "0px", paddingTop: "0px", paddingBottom: "0px", borderBottomWidth: "0px" },
      ],
      { duration: quiet ? 160 : 340, easing: "cubic-bezier(0.23, 1, 0.32, 1)", fill: "forwards" },
    );
    await fold.finished;
  };

  /** Puts the row back as it was, when the save failed. */
  const undo = () => {
    const row = ref.current;
    row?.getAnimations().forEach((a) => a.cancel());
    if (row) row.style.overflow = "";
    setActive(false);
  };

  return { ref, active, play, undo };
}

/** The tick mark, drawn in. */
export function TickMark() {
  return (
    <svg className="td-tick-mark" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4.5 10.5l3.5 3.5 7.5-8" />
    </svg>
  );
}

const SPARK_COLOURS = ["var(--td-glow)", "var(--td-glow)", "var(--good)", "var(--td-spark-light)", "var(--td-spark-light)", "var(--accent)"];

type Spark = { streak: boolean; a: number; d: number; s: number; t: number; w: number; c: string };

/**
 * Two bursts from the box: a fast ring of streaks, then a wider, slower spray
 * of dots. Every value is jittered, so no two ticks look the same.
 */
function makeSparks(): Spark[] {
  const pick = () => SPARK_COLOURS[Math.floor(Math.random() * SPARK_COLOURS.length)]!;
  const streaks = Array.from({ length: 10 }, (_, i) => ({
    streak: true, a: i * 36 + (Math.random() * 14 - 7), d: 22 + Math.random() * 10, s: 3 + Math.random() * 2, t: 420 + Math.random() * 120, w: 60 + Math.random() * 40, c: pick(),
  }));
  const dots = Array.from({ length: 18 }, () => ({
    streak: false, a: Math.random() * 360, d: 26 + Math.random() * 26, s: 2.5 + Math.random() * 3.5, t: 620 + Math.random() * 380, w: 90 + Math.random() * 120, c: pick(),
  }));
  return [...streaks, ...dots];
}

/** The glow, rings and sparks, inside the box. Only drawn while a tick plays. */
export function TickBurst() {
  const sparks = useMemo(makeSparks, []);
  return (
    <>
      <span className="td-tick-halo" />
      <span className="td-tick-fill" />
      <span className="td-tick-ring" />
      <span className="td-tick-ring two" />
      {sparks.map((p, i) => (
        <i
          key={i}
          className={`td-spark${p.streak ? " streak" : ""}`}
          style={{ "--a": `${p.a.toFixed(1)}deg`, "--d": `${p.d.toFixed(1)}px`, "--s": `${p.s.toFixed(1)}px`, "--t": `${p.t.toFixed(0)}ms`, "--w": `${p.w.toFixed(0)}ms`, "--c": p.c } as CSSProperties}
        />
      ))}
    </>
  );
}
